// src/composables/useApi.ts
import { api } from 'src/boot/axios';
import { Loading, Notify } from 'quasar';
import type { Method, AxiosRequestConfig, AxiosError } from 'axios';

export function useApi() {
  const request = async (
    method: Method,
    endpoint: string,
    payload?: unknown,
    options?: AxiosRequestConfig,
  ) => {
    Loading.show({
      message: 'Loading',
    });
    try {
      const response = await api({
        method,
        url: endpoint,
        data: payload,
        ...options,
      });
      return {
        response: response,
        data: response.data.data,
        meta: response.data.meta,
      };
    } catch (err) {
      const error = err as AxiosError;
      const errorMessage =
        (error.response?.data as { message: string })?.message ||
        'An unknown error occurred';
      Notify.create({
        type: 'negative',
        multiLine: true,
        message: errorMessage,
        position: 'top',
      });
      throw err;
    } finally {
      Loading.hide();
    }
  };

  const get = async (endpoint: string, options?: AxiosRequestConfig) => {
    return await request('get', endpoint, null, options);
  };

  const post = async (
    endpoint: string,
    payload?: unknown,
    options?: AxiosRequestConfig,
    showNotification = true,
  ) => {
    const response = await request('post', endpoint, payload, options);

    if (showNotification) {
      const [route] = endpoint.split('/');
      const message = `${route.charAt(0).toUpperCase()}${route.slice(1)} saved`;
      Notify.create({
        type: 'positive',
        multiLine: true,
        message,
        position: 'top',
      });
    }

    return response;
  };

  const put = async (
    endpoint: string,
    payload: unknown,
    options?: AxiosRequestConfig,
  ) => {
    const response = await request('put', endpoint, payload, options);
    const [route] = endpoint.split('/');
    const message = `${route.charAt(0).toUpperCase()}${route.slice(1)} saved`;
    Notify.create({
      type: 'positive',
      multiLine: true,
      message,
      position: 'top',
    });
    return response;
  };

  const del = async (endpoint: string, options?: AxiosRequestConfig) => {
    return await request('delete', endpoint, null, options);
  };

  return {
    get,
    post,
    put,
    del,
  };
}
